import React from "react";
import styles from "./glow-indicator.module.css";
import { withSafeRender } from "../safe-render";

/**
 * Glow Indicator — a radial gradient blur effect for variant highlights and
 * active states. Renders as a soft glow bar with configurable color by variant.
 *
 * FUSION ECLIPSE, page UI Kit, section "🔹 Data display" (10188:17455):
 * - Indigo
 * - Blue
 * - Green
 * - Yellow
 * - Purple
 * - Slate
 */

export type GlowVariant = "indigo" | "blue" | "green" | "yellow" | "purple" | "slate";

export interface GlowIndicatorProps {
  /** Variant determines the gradient color. Defaults to "slate". */
  variant?: GlowVariant;
  /** Width of the glow indicator in pixels. Defaults to 290px. */
  width?: number | string;
  /** Height of the blur element in pixels. Defaults to 32px. */
  blurHeight?: number | string;
  /** Height of the bar element in pixels. Defaults to 4px. */
  barHeight?: number | string;
  /** CSS class name to apply to the wrapper. */
  className?: string;
}

const VARIANTS: Record<GlowVariant, true> = {
  indigo: true,
  blue: true,
  green: true,
  yellow: true,
  purple: true,
  slate: true
};

/** The value if it is one of the closed set above, the default if it is not. */
function published<T extends string>(
  set: Record<T, true>,
  value: string,
  fallback: T
): T {
  return Object.prototype.hasOwnProperty.call(set, value) ? (value as T) : fallback;
}

function GlowIndicatorImpl({
  variant = "slate",
  width = 290,
  blurHeight = 32,
  barHeight = 4,
  className
}: GlowIndicatorProps) {
  const resolvedVariant = published(VARIANTS, variant, "slate");

  const classes = [styles.container, styles[resolvedVariant], className]
    .filter(Boolean)
    .join(" ");

  const style: React.CSSProperties = {
    width: typeof width === "number" ? `${width}px` : width,
    "--glow-blur-height": typeof blurHeight === "number" ? `${blurHeight}px` : blurHeight,
    "--glow-bar-height": typeof barHeight === "number" ? `${barHeight}px` : barHeight
  } as React.CSSProperties & Record<string, any>;

  return (
    <div className={classes} style={style}>
      <div className={styles.blur} aria-hidden="true" />
      <div className={styles.bar} aria-hidden="true" />
    </div>
  );
}
export const GlowIndicator = withSafeRender(GlowIndicatorImpl, "GlowIndicator");

export default GlowIndicator;
